import { api, ApiError } from "../api/client";
import { t } from "../i18n";
import { idb } from "./idb";
import { isOnline } from "./network";
import { keepOwn, keptOwn } from "./offlineCache";
import { deviceLabel, freeNumber, fyOfDay, liveBlocks, numbersHeld, RELEASE_NOTE, serverNow, SIGNED_OUT_NOTE, skewOf, type KeptBlock } from "./edit-core";
import type { OutboxItem } from "./outbox-core";

/*
 * Invoice numbers reserved to this device (wave 7, server/offline-sync.js), so an invoice can be issued with no connection.
 *
 *   While online, a person whose role may issue invoices keeps a block of numbers for this device (the block size is
 *   Settings › Number series). It is asked for after sign-in and after each replay, and again when it is used up.
 *   The block is kept like the offline copies (offlineCache.ts, per person and organization; deleted on sign-out, at the
 *   next sign-in of someone else, and when the person leaves the organization). Sign-out releases the unused numbers on
 *   the server first when it can; otherwise they wait in the numbering report to be released or cancelled.
 *   The device id is a random id in "prefs" (nothing personal), so the server accepts a number only from the device it was
 *   reserved to.
 *
 * Wave 8 (docs/implemented-offline8.md):
 *   - A block lasts what Settings › Number series says (block_hours, default 24): the device issues with none of its numbers
 *     after its expires_at, and keeps it on the device that long. Each reconnect refreshes it (outbox.ts flush).
 *   - `spent`: numbers this device has put on an invoice that the server has not taken yet (an issue waiting, in conflict, or
 *     refused). They are never offered again, are sent as `held` with each refresh so the server keeps them out of the pool,
 *     and at sign-out are marked "used on device, refused" on the server instead of being released.
 *
 * Wave 9 (docs/implemented-offline9.md): a block ends by the server's clock. Each refresh sends this device's clock and keeps
 * `skew`, the server's clock minus this device's (from the answer's server_time, at the middle of the request), so a block's
 * end is compared with the server's time, not with a device clock that is wrong. Each issue carries the device's clock
 * (`issued_at`), which the server corrects by the skew it recorded when the block was reserved.
 */

type Blocks = { blocks: KeptBlock[]; allowed: boolean; at: number; spent?: string[]; hours?: number; skew?: number };
type Pref = { key: string; value: string; at: number };
type ServerBlock = { id: string; period: string; free: string[]; expires_at?: string; expired?: boolean };

const NAME = "invoice-blocks";
const DEVICE = "device-id";
let asking: Promise<void> | null = null;

/** This device's id: made once, kept across sign-outs. */
export async function deviceId(): Promise<string> {
  const p = await idb.get<Pref>("prefs", DEVICE).catch(() => undefined);
  if (p?.value) return p.value;
  const value = `dev_${crypto.randomUUID().replaceAll("-", "")}`;
  await idb.put<Pref>("prefs", { key: DEVICE, value, at: Date.now() }).catch(() => undefined);
  return value;
}

export const keptBlocks = () => keptOwn<Blocks>(NAME);

const HOUR = 3_600_000;
/** How long the kept block stays on the device: until its last block ends by the server's clock (at least an hour). */
const keepFor = (blocks: KeptBlock[], hours = 24, skew = 0) => Math.max(HOUR, ...blocks.map((b) => (b.expires_at ? Date.parse(b.expires_at) - serverNow(skew) : hours * HOUR)));

/**
 * Online: the device's block, from the server (the open one, or a new one when it is used up or its time is over). A role
 * that may not issue keeps none. Quiet: a failure leaves what is kept. `held`: numbers the outbox still holds (outbox.ts).
 */
export function refreshBlocks(held: string[] = []): Promise<void> {
  if (!isOnline()) return Promise.resolve();
  asking ??= (async () => {
    try {
      const kept = await keptBlocks().catch(() => null);
      const spent = [...new Set([...(kept?.spent ?? []), ...held])];
      const sent = Date.now();
      const out = await api<{ blocks: ServerBlock[]; block_hours?: number; server_time?: string }>("POST", "/admin/offline/blocks", { device_id: await deviceId(), device_label: deviceLabel(typeof navigator === "undefined" ? "" : navigator.userAgent), held: spent, device_time: new Date(sent).toISOString() });
      // Wave 9: the server's clock minus this device's, read at the middle of the request.
      const skew = skewOf(out.server_time, sent, Date.now()) ?? kept?.skew ?? 0;
      const blocks = out.blocks.map((b) => ({ id: b.id, period: b.period, free: b.free, expires_at: b.expires_at ?? null }));
      // A spent number the server no longer has reserved (used, refused) needs no keeping out any more.
      const reserved = new Set(blocks.flatMap((b) => b.free));
      await keepOwn<Blocks>(NAME, { blocks, allowed: true, at: Date.now(), spent: spent.filter((n) => reserved.has(n)), hours: out.block_hours, skew }, keepFor(blocks, out.block_hours, skew));
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) await keepOwn<Blocks>(NAME, { blocks: [], allowed: false, at: Date.now() });
    } finally {
      asking = null;
    }
  })();
  return asking;
}

/** The number an invoice dated `day` would be issued with offline, or why it can't be. */
export async function nextOfflineNumber(items: OutboxItem[], org: number, day: string): Promise<{ number: string; block: string } | { refused: string }> {
  const kept = await keptBlocks();
  if (kept && !kept.allowed) return { refused: t("Your role can't issue invoices, so it can't issue them offline either.") };
  const any = kept?.blocks ?? [];
  if (!any.length) return { refused: t("No invoice numbers are reserved on this device. Go online once so it can reserve a block.") };
  // Wave 8: a block whose time is over issues nothing more (its numbers go back to the pool at the next refresh). Wave 9: by
  // the server's clock.
  const all = liveBlocks(any, serverNow(kept?.skew));
  if (!all.length) return { refused: t("This device's reserved invoice numbers expired. Go online once so it can reserve a fresh block, or save the invoice as a draft.") };
  const period = all.find((b) => b.period === fyOfDay(day) || b.period === "all")?.period;
  if (!period) return { refused: t("The numbers on this device belong to another financial year than the invoice date.") };
  const held = new Set([...numbersHeld(items, org), ...(kept?.spent ?? [])]);
  return freeNumber(all, held, period) ?? { refused: t("This device's reserved invoice numbers are used up. Issue it when you're back online, or save it as a draft.") };
}

/** Wave 8: what the offline screen says about the block: numbers left to issue with, and until when. */
export async function blockState(items: OutboxItem[], org: number): Promise<{ allowed: boolean; left: number; until: string | null } | null> {
  const kept = await keptBlocks().catch(() => null);
  if (!kept) return null;
  const all = liveBlocks(kept.blocks, serverNow(kept.skew));
  const held = new Set([...numbersHeld(items, org), ...(kept.spent ?? [])]);
  const left = all.reduce((n, b) => n + b.free.filter((x) => !held.has(x)).length, 0);
  const until = all.map((b) => b.expires_at).filter((x): x is string => Boolean(x)).sort().pop() ?? null;
  return { allowed: kept.allowed, left, until };
}

/** Wave 8: a number this device just put on an invoice; it is never offered again (see `spent` above). */
export async function spendNumber(number: string) {
  const kept = await keptBlocks().catch(() => null);
  if (!kept) return;
  await keepOwn<Blocks>(NAME, { ...kept, spent: [...new Set([...(kept.spent ?? []), number])] }, keepFor(kept.blocks, kept.hours, kept.skew));
}

/** Wave 9: the server's time now, by this device's clock and the skew kept at the last refresh. */
export async function serverClock(): Promise<number> {
  return serverNow((await keptBlocks().catch(() => null))?.skew);
}

/**
 * Sign-out while online: the unused numbers go back to the server's pool, so nobody has to cancel them. Wave 8: a number
 * this device put on an invoice that was never sent (`held`: still in the outbox, which sign-out deletes) is marked used on
 * the device and refused first, so it never goes to another invoice.
 */
export async function releaseBlocks(held: string[] = []) {
  const kept = await keptBlocks().catch(() => null);
  if (!kept?.blocks.length || !isOnline()) return;
  const onInvoices = [...new Set([...(kept.spent ?? []), ...held])].filter((n) => kept.blocks.some((b) => b.free.includes(n)));
  if (onInvoices.length) await api("POST", "/admin/offline/numbers/refuse", { device_id: await deviceId(), numbers: onInvoices, note: SIGNED_OUT_NOTE }).catch(() => undefined);
  await Promise.all(kept.blocks.map((b) => api("POST", `/admin/offline/blocks/${b.id}/release`, { note: RELEASE_NOTE }).catch(() => undefined)));
}
